import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import slugify from 'slugify';

// Config.
import { FILTER_OPTION_SHAPE } from '../../../config/prop-types';

// Images.
import iconCaret from '../../images/icon-caret.png';

// Services.
import getThemeColor from '../../js/services/get-theme-color';
import toggleSelected from '../../js/services/filters/toggle-selected';

// Styles.
import './filter.scss';

const Filter = ({
  enableSeeMoreButton,
  initialOpen,
  label,
  onChange,
  options,
  selected,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isTopFiltersOnly, setIsTopFiltersOnly] = useState(enableSeeMoreButton);

  /**
   * An event handler for changes to the checkboxes.
   * @param {Event} event - The change event that is fired when the value of the checkbox changes.
   */
  const handleChange = (event) => onChange(toggleSelected(
    selected,
    event.target.value,
  ));

  return (
    <fieldset className="filter">
      <legend>
        <button
          aria-controls={`filter-${slugify(label, { lower: true })}`}
          aria-expanded={isOpen}
          className="filter__trigger"
          onClick={(event) => {
            event.preventDefault();
            setIsOpen(! isOpen);
          }}
        >
          <span>{label}</span>
          <span className="filter__icon">
            <img src={iconCaret} alt="" />
          </span>
        </button>
      </legend>
      <div
        aria-hidden={! isOpen}
        className="filter__content"
        id={`filter-${slugify(label, { lower: true })}`}
      >
        {options.slice(0, (isTopFiltersOnly ? 4 : options.length)).map(({ label: optionLabel, value }) => { // eslint-disable-line max-len
          // eslint-disable-next-line max-len
          const optionSlug = `${slugify(optionLabel, { lower: true })}-${slugify(String(value), { lower: true })}`;
          // Remove any count in parens and white space.
          const optionLabelForTheme = optionLabel.replace(/\s\((.+?)\)/g, '');
          const optionThemeColor = getThemeColor(optionLabelForTheme);
          const isChecked = selected.includes(value);
          return (
            <div
              className={classnames(
                'filter__option',
                {
                  [`filter__option--type--${optionThemeColor}`]:
                    !! optionThemeColor,
                  'filter__option--type--none': ! optionThemeColor,
                  'filter__option--type--boxed': !! optionThemeColor,
                }
              )}
              key={value}
            >
              <label
                htmlFor={optionSlug}
                className="filter__option-label"
              >
                <input
                  checked={isChecked}
                  className="filter__option-checkbox"
                  id={optionSlug}
                  onChange={handleChange}
                  type="checkbox"
                  value={value}
                />
                <span>{optionLabel}</span>
              </label>
            </div>
          );
        })}
        {(isTopFiltersOnly && 4 < options.length) ? (
          <button
            className="filter-showme-button"
            onClick={(event) => {
              event.preventDefault();
              setIsTopFiltersOnly(! isTopFiltersOnly);
            }}
          >
            See More +
          </button>
        ) : null}
      </div>
    </fieldset>
  );
};

Filter.defaultProps = {
  enableSeeMoreButton: false,
  initialOpen: false,
};

Filter.propTypes = {
  enableSeeMoreButton: PropTypes.bool,
  initialOpen: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape(FILTER_OPTION_SHAPE)).isRequired,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ])).isRequired,
};

export default Filter;
